import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { FileText, Scale, ChevronDown, ChevronRight, Calendar, Users, Gavel, HelpCircle, ArrowUpDown } from 'lucide-react';
import useAuth from '@/hooks/useAuth';
import { AppLayout } from "@/components/layout/AppLayout";
import { avtalListAvtal, avtalGetAvtalDetail, judgmentsReadJudgments, judgmentsGetJudgmentClassification } from '@/client';
import type { JudgmentPublic, JudgmentClassificationResponse } from '@/client';
import { handleAuthError } from '@/lib/auth-utils';
import { cn } from '@/lib/utils';

interface Paragraph {
  id: string;
  paragraph: string;
  title: string;
  content: string | null;
  sort_order: number;
  legal_provision_count: number;
}

interface Chapter {
  id: string;
  chapter: string;
  title: string;
  sort_order: number;
  paragraphs: Paragraph[];
}

interface Contract {
  id: string;
  name: string;
  title: string;
  description: string | null;
  created_at: string;
  chapters: Chapter[];
}

interface JudgmentWithClassification {
  id: string;
  filename: string;
  målnummer?: string;
  domstol?: string;
  instans?: string;
  dom_datum?: string;
  parter?: string;
  bakgrund?: string;
  avtal_typ?: string;
  tvist_om_standardvillkor?: boolean;
  upload_date: string;
  relevant_bedomning?: string; // Court's assessment for the selected paragraph
  relevant_frageställning?: string; // Legal question/issue for the selected paragraph
  converted_from?: string; // Original contract type if converted
  provision_converted_from?: string; // Original paragraph if provision was converted
}

  const getInitialCollapsedChapters = (contract: Contract): Set<string> => {
    return new Set(contract.chapters.map(chapter => chapter.chapter));
  };

  // Define court hierarchy for sorting
  const getCourtPriority = (instans: string): number => {
    const normalized = instans?.toLowerCase() || '';
    if (normalized.includes('högsta domstolen') || normalized.includes('hd')) return 1;
    if (normalized.includes('hovrätt')) return 2;
    if (normalized.includes('tingsrätt')) return 3;
    return 4; // Unknown courts go last
  };export const ContractExplorer = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialLoadComplete = useRef(false);
  
  // Fetch contract list from API
  const { data: contractList, isLoading: contractsLoading } = useQuery({
    queryKey: ["contracts"],
    queryFn: () => avtalListAvtal(),
  });

  const [selectedContractId, setSelectedContractId] = useState<string | null>(null);
  const [selectedParagraph, setSelectedParagraph] = useState<string | null>(null);
  const [collapsedChapters, setCollapsedChapters] = useState<Set<string>>(new Set());
  const [relatedJudgments, setRelatedJudgments] = useState<JudgmentWithClassification[]>([]);
  const [loadingJudgments, setLoadingJudgments] = useState(false);
  const [deepLinkProcessed, setDeepLinkProcessed] = useState(false);

  // Set initial contract when contracts are loaded
  useEffect(() => {
    if (contractList?.data && contractList.data.length > 0 && !selectedContractId) {
      // Check URL params first
      const contractParam = searchParams.get('contract');
      if (contractParam) {
        const contract = contractList.data.find(c => c.name === contractParam);
        if (contract) {
          setSelectedContractId(contract.id);
          return;
        }
      }
      // Default to first contract if no URL param or not found
      setSelectedContractId(contractList.data[0].id);
    }
  }, [contractList, selectedContractId, searchParams]);

  // Fetch selected contract details
  const { data: selectedContract, isLoading: contractLoading } = useQuery({
    queryKey: ["contract", selectedContractId],
    queryFn: () => avtalGetAvtalDetail({ avtalId: selectedContractId! }),
    enabled: !!selectedContractId,
  });

  // Process deep linking and update collapsed chapters when contract changes
  useEffect(() => {
    if (!selectedContract || deepLinkProcessed) return;
    
    const provisionParam = searchParams.get('provision');
    if (provisionParam && !initialLoadComplete.current) {
      // Find the paragraph that matches the provision parameter
      let paragraphFound = false;
      const newCollapsedChapters = new Set<string>();
      
      for (const chapter of selectedContract.chapters) {
        let shouldExpand = false;
        
        for (const paragraph of chapter.paragraphs) {
          // Match provision format like "5 § 1" or "Kap 5 § 1"
          if (paragraph.paragraph === provisionParam || 
              paragraph.paragraph === `Kap ${provisionParam}` ||
              paragraph.paragraph.endsWith(provisionParam)) {
            setSelectedParagraph(paragraph.id);
            shouldExpand = true;
            paragraphFound = true;
            
            // Scroll to the paragraph after a short delay
            setTimeout(() => {
              const element = document.getElementById(`paragraph-${paragraph.id}`);
              if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            break;
          }
        }
        
        // Only collapse chapters that don't contain the selected provision
        if (!shouldExpand) {
          newCollapsedChapters.add(chapter.chapter);
        }
      }
      
      setCollapsedChapters(newCollapsedChapters);
      setDeepLinkProcessed(true);
      initialLoadComplete.current = true;
    } else {
      // Default behavior: collapse all chapters
      setCollapsedChapters(getInitialCollapsedChapters(selectedContract));
    }
  }, [selectedContract, searchParams, deepLinkProcessed]);

  // Fetch related judgments when a paragraph is selected
  useEffect(() => {
    const fetchRelatedJudgments = async () => {
      if (!selectedParagraph || !user) {
        setRelatedJudgments([]);
        return;
      }

      setLoadingJudgments(true);
      try {
        // Fetch judgments filtered by paragraph ID
        const judgmentsResponse = await judgmentsReadJudgments({
          paragraphId: selectedParagraph,
          limit: 100
        });

        const judgmentsWithClassification: JudgmentWithClassification[] = [];

        // Fetch classification data for each judgment
        for (const judgment of judgmentsResponse?.data || []) {
          try {
            const classificationResponse = await judgmentsGetJudgmentClassification({
              judgmentId: judgment.id
            });

            const classificationData = classificationResponse?.classification;
            const legalProvisions = classificationResponse?.legal_provisions || [];
            
            // Find the legal provision that relates to the selected paragraph
            const relevantProvision = legalProvisions.find(provision => 
              provision.avtal_paragraph?.id === selectedParagraph
            );
            
            judgmentsWithClassification.push({
              id: judgment.id,
              filename: judgment.filename,
              målnummer: classificationData?.målnummer,
              domstol: classificationData?.domstol,
              instans: classificationData?.instans,
              dom_datum: classificationData?.dom_datum,
              parter: classificationData?.parter,
              bakgrund: classificationData?.bakgrund,
              avtal_typ: classificationData?.avtal_typ,
              tvist_om_standardvillkor: classificationData?.tvist_om_standardvillkor,
              upload_date: judgment.upload_date,
              relevant_bedomning: relevantProvision?.bedomning,
              relevant_frageställning: relevantProvision?.frågeställning,
              converted_from: classificationData?.converted_from,
              provision_converted_from: relevantProvision?.converted_from
            });
          } catch (err) {
            console.warn(`Failed to load classification for judgment ${judgment.id}:`, err);
            
            // Handle auth errors immediately
            if (handleAuthError(err)) {
              return;
            }
            
            // Add judgment without classification as fallback
            judgmentsWithClassification.push({
              id: judgment.id,
              filename: judgment.filename,
              upload_date: judgment.upload_date
            });
          }
        }

        // Sort judgments by court hierarchy (higher courts first) and then by date (newest first)
        const sortedJudgments = judgmentsWithClassification.sort((a, b) => {
          // First sort by court hierarchy
          const priorityA = getCourtPriority(a.instans || '');
          const priorityB = getCourtPriority(b.instans || '');
          
          if (priorityA !== priorityB) {
            return priorityA - priorityB; // Lower number = higher priority
          }
          
          // If same court level, sort by date (newest first)
          const dateA = new Date(a.dom_datum || a.upload_date).getTime();
          const dateB = new Date(b.dom_datum || b.upload_date).getTime();
          return dateB - dateA;
        });

        setRelatedJudgments(sortedJudgments);
      } catch (err) {
        console.error('Failed to load related judgments:', err);
        
        // Handle auth errors immediately
        if (handleAuthError(err)) {
          return;
        }
        
        setRelatedJudgments([]);
      } finally {
        setLoadingJudgments(false);
      }
    };

    fetchRelatedJudgments();
  }, [selectedParagraph, user]);

  const handleParagraphClick = (paragraphId: string) => {
    const newSelected = paragraphId === selectedParagraph ? null : paragraphId;
    setSelectedParagraph(newSelected);
    
    // Update URL with the selected provision
    if (newSelected && selectedContract) {
      const paragraph = selectedContract.chapters
        .flatMap(c => c.paragraphs)
        .find(p => p.id === newSelected);
      
      if (paragraph) {
        setSearchParams({
          contract: selectedContract.name,
          provision: paragraph.paragraph
        });
      }
    } else {
      // Clear provision param if deselecting
      if (selectedContract) {
        setSearchParams({ contract: selectedContract.name });
      } else {
        setSearchParams({});
      }
    }
  };

  const toggleChapter = (chapterNumber: string) => {
    const newCollapsed = new Set(collapsedChapters);
    if (newCollapsed.has(chapterNumber)) {
      newCollapsed.delete(chapterNumber);
    } else {
      newCollapsed.add(chapterNumber);
    }
    setCollapsedChapters(newCollapsed);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  const handleJudgmentClick = (judgmentId: string) => {
    // Pass current selection state when navigating to judgment
    const state: any = {};
    if (selectedContract) {
      state.fromContract = selectedContract.name;
    }
    if (selectedParagraph) {
      const paragraph = selectedContract?.chapters
        .flatMap(c => c.paragraphs)
        .find(p => p.id === selectedParagraph);
      if (paragraph) {
        state.fromProvision = paragraph.paragraph;
      }
    }
    navigate(`/avgorande/${judgmentId}`, { state });
  };


  return (
    <AppLayout>
      <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col gap-6">
        <div>
          <h2 className="text-lg md:text-xl font-semibold mb-3 text-primary drop-shadow-sm">Välj avtal</h2>
          {contractsLoading ? (
            <div className="flex space-x-2">
              <div className="animate-pulse bg-muted h-10 w-24 rounded"></div>
              <div className="animate-pulse bg-muted h-10 w-24 rounded"></div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-3">
              {contractList?.data?.map((contract) => {
                const isActive = selectedContractId === contract.id;
                
                return (
                  <Button
                    key={contract.id}
                    variant="secondary"
                    onClick={() => {
                      setSelectedContractId(contract.id);
                      setSelectedParagraph(null);
                      // Update URL with new contract selection
                      setSearchParams({ contract: contract.name });
                    }}
                    className={cn(
                      "rounded-full border border-primary/40 bg-background/80 px-5 py-2 text-sm font-medium uppercase tracking-wide text-primary transition-smooth shadow-sm hover:border-primary/60 hover:bg-primary/10 hover:text-primary",
                      isActive && "border-transparent bg-primary text-primary-foreground shadow-card-legal hover:bg-primary/90"
                    )}
                  >
                    {contract.name}
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex-1 min-h-0 flex flex-col gap-6 lg:flex-row">
          {/* Left Panel - Contract Structure */}
          <Card className="bg-gradient-card shadow-card-legal flex flex-col flex-1 min-h-0">
            <div className="p-6 pb-4 border-b border-border flex-shrink-0">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold text-foreground">
                  {contractLoading ? "Laddar..." : selectedContract?.name} - Avtalsstruktur
                </h3>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 pt-4">
            {contractLoading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse space-y-2">
                    <div className="bg-muted h-8 rounded"></div>
                    <div className="ml-4 space-y-2">
                      <div className="bg-muted/70 h-6 rounded"></div>
                      <div className="bg-muted/70 h-6 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : selectedContract ? (
              <div className="space-y-4">
                {selectedContract.chapters.map((chapter) => {
                  const isCollapsed = collapsedChapters.has(chapter.chapter);
                  
                  return (
                    <div key={chapter.id} className="animate-fade-in">
                      <div 
                        className="flex items-center space-x-2 cursor-pointer hover:bg-primary/5 rounded-lg p-2 -ml-2 transition-smooth"
                        onClick={() => toggleChapter(chapter.chapter)}
                      >
                        {isCollapsed ? (
                          <ChevronRight className="h-4 w-4 text-primary" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-primary" />
                        )}
                        <h4 className="font-semibold text-primary">
                          Kapitel {chapter.chapter}: {chapter.title}
                        </h4>
                        <Badge variant="outline" className="text-xs border-primary/30 bg-primary/5 text-primary">
                          {chapter.paragraphs.length} §§
                        </Badge>
                      </div>
                    
                    {!isCollapsed && (
                      <div className="space-y-2 ml-4 mt-2">
                        {chapter.paragraphs.map((paragraph) => {
                          const isSelected = selectedParagraph === paragraph.id;
                          
                          return (
                            <div
                              key={paragraph.id}
                              id={`paragraph-${paragraph.id}`}
                              className={`p-3 rounded-lg border cursor-pointer transition-smooth ${
                                isSelected
                                  ? 'border-primary bg-primary/15 text-primary shadow-card-legal'
                                  : 'bg-muted/60 border-border hover:border-primary/40 hover:bg-primary/10'
                              }`}
                              onClick={() => handleParagraphClick(paragraph.id)}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="font-semibold text-foreground">
                                      {paragraph.paragraph}
                                    </span>
                                    <Badge variant="secondary" className="text-xs border border-primary/20 bg-primary/10 text-primary">
                                      {paragraph.legal_provision_count} avgörande{paragraph.legal_provision_count !== 1 ? 'n' : ''}
                                    </Badge>
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {paragraph.title}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-muted-foreground">
                  Inga avtal tillgängliga eller avtal kunde inte laddas.
                </p>
              </div>
            )}
            </div>
          </Card>

          {/* Right Panel - Legal Provisions */}
          <Card className="bg-gradient-card shadow-card-legal flex flex-col flex-1 min-h-0">
            <div className="p-6 pb-4 border-b border-border flex-shrink-0">
              <h3 className="text-xl font-semibold text-foreground">
                Avgöranden
              </h3>
            </div>
            
            <div className="flex-1 overflow-y-auto p-6 pt-4">
            
            {!selectedParagraph ? (
              <div className="text-center py-12">
                <div className="text-muted-foreground mb-2">
                  <Scale className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>Välj en bestämmelse för att se avgöranden</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="mb-4 flex items-center justify-between">
                  <Badge variant="outline" className="border-primary/40 bg-primary/10 text-primary">
                    {selectedContract?.chapters.find(c => 
                      c.paragraphs.some(p => p.id === selectedParagraph)
                    )?.paragraphs.find(p => p.id === selectedParagraph)?.paragraph || 'Paragraf'}
                  </Badge>
                  <span className="text-sm text-muted-foreground">
                    {relatedJudgments.length} avgörande{relatedJudgments.length !== 1 ? 'n' : ''}
                  </span>
                </div>
                
                {loadingJudgments ? (
                  <div className="text-center py-8">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Laddar relaterade avgöranden...</p>
                  </div>
                ) : relatedJudgments.length > 0 ? (
                  <div className="space-y-4">
                    {relatedJudgments.map((judgment) => (
                      <Card 
                        key={judgment.id}
                        className="p-4 hover:shadow-card-legal transition-smooth cursor-pointer border border-transparent border-l-4 border-primary/70 hover:border-primary"
                        onClick={() => handleJudgmentClick(judgment.id)}
                      >
                        <div className="space-y-2">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-primary">
                                {judgment.målnummer || judgment.filename}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                {judgment.domstol || 'Okänd domstol'}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {judgment.avtal_typ && (
                                <Badge variant="secondary" className="text-xs border border-primary/30 bg-primary/10 text-primary">
                                  {judgment.avtal_typ}
                                  {judgment.converted_from && (
                                    <span className="ml-1 text-accent" title={`Konverterad från ${judgment.converted_from}`}>
                                      ↑
                                    </span>
                                  )}
                                </Badge>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                            {judgment.dom_datum && (
                              <div className="flex items-center space-x-1">
                                <Calendar className="h-3 w-3 text-primary/70" />
                                <span>{formatDate(judgment.dom_datum)}</span>
                              </div>
                            )}
                          </div>
                          
                          {judgment.parter && (
                            <div className="flex items-start space-x-2 text-xs">
                              <Users className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary/70" />
                              <span className="text-muted-foreground line-clamp-1">{judgment.parter}</span>
                            </div>
                          )}
                          
                          {judgment.relevant_frageställning && (
                            <div className="text-xs text-muted-foreground mb-2">
                              <div className="flex items-start space-x-2">
                                <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary/70" />
                                <div>
                                  <span className="font-semibold text-primary">Frågeställning:</span>
                                  {judgment.provision_converted_from && (
                                    <span className="ml-2 text-xs text-accent" title={`Ursprunglig paragraf: ${judgment.provision_converted_from}`}>
                                      <ArrowUpDown className="h-3 w-3 inline mr-1" />
                                      {judgment.provision_converted_from}
                                    </span>
                                  )}
                                  <p className="line-clamp-2 leading-relaxed mt-1">
                                    {judgment.relevant_frageställning}
                                  </p>
                                </div>
                              </div>
                            </div>
                          )}
                          
                          {judgment.relevant_bedomning ? (
                            <div className="text-xs text-muted-foreground">
                              <div className="flex items-start space-x-2">
                                <Gavel className="h-3 w-3 mt-0.5 flex-shrink-0 text-primary/70" />
                                <div>
                                  <span className="font-semibold text-primary">Domstolens bedömning:</span>
                                  <p className="line-clamp-3 leading-relaxed mt-1">
                                    {judgment.relevant_bedomning}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : judgment.bakgrund && (
                            <p className="text-xs text-muted-foreground line-clamp-2 leading-relaxed">
                              {judgment.bakgrund}
                            </p>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-muted/20 rounded-lg border border-muted-foreground/20">
                    <Scale className="h-8 w-8 mx-auto mb-3 text-muted-foreground/50" />
                    <p className="text-muted-foreground text-sm">
                      Inga avgöranden hittades för denna paragraf
                    </p>
                  </div>
                )}
              </div>
            )}
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
};
